import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';

import type { RecipientStatus } from '@/types/roc/models/RecipientStatus';
import { RecipientStatus as RecipientStatusEnum } from '@/types/roc/models/RecipientStatus';

import { computeAge, formatDob } from '../lib/referralDisplay';

interface ReferralSummaryProps {
  patientName: string;
  dob: Date | null | undefined;
  senderFacilityName: string;
  referredForLabel?: string | null;
  dischargeDate?: string | null;
  status: RecipientStatus;
  disabled?: boolean;
  onSelect: (status: RecipientStatus) => void;
}

const ACTIONS: Array<{
  label: string;
  status: RecipientStatus;
  color: 'success' | 'warning' | 'error';
  Icon: React.ElementType;
}> = [
  {
    label: 'Accept',
    status: RecipientStatusEnum.Accepted,
    color: 'success',
    Icon: CheckCircleOutlineIcon
  },
  {
    label: 'Need More Info',
    status: RecipientStatusEnum.NeedsMoreInfo,
    color: 'warning',
    Icon: HelpOutlineIcon
  },
  {
    label: 'Decline',
    status: RecipientStatusEnum.Declined,
    color: 'error',
    Icon: CancelOutlinedIcon
  }
];

export function ReferralSummary({
  patientName,
  dob,
  senderFacilityName,
  referredForLabel,
  dischargeDate,
  status,
  disabled,
  onSelect
}: ReferralSummaryProps) {
  const age = computeAge(dob);
  const demographics = [
    age !== null ? `${age} yrs` : null,
    `DOB ${formatDob(dob)}`
  ]
    .filter(Boolean)
    .join(' · ');

  const referredForText = [
    referredForLabel,
    dischargeDate ? `Discharge ${dischargeDate}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
      >
        <Stack direction="row" alignItems="flex-start" gap={2}>
          <Stack gap={0.5}>
            <Typography
              sx={{ fontSize: 28, fontWeight: 700, color: 'text.primary' }}
            >
              {patientName}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              {demographics}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              flexWrap="wrap"
              sx={{ mt: 0.25 }}
            >
              <Stack direction="row" alignItems="flex-start" gap={0.75}>
                <ApartmentOutlinedIcon
                  sx={{ fontSize: 16, color: 'text.secondary', mt: '2.5px' }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: 16, sm: 14 },
                    color: 'text.secondary'
                  }}
                >
                  Referral from{' '}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: 16, sm: 14 },
                      fontWeight: 600,
                      color: 'text.primary'
                    }}
                  >
                    {senderFacilityName}
                  </Typography>
                </Typography>
              </Stack>
              {referredForText && (
                <>
                  <Box
                    sx={{
                      width: '1px',
                      height: 16,
                      bgcolor: 'divider',
                      display: { xs: 'none', sm: 'block' }
                    }}
                  />
                  <Stack direction="row" alignItems="flex-start" gap={0.75}>
                    <HomeOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: 'text.secondary',
                        mt: '2.5px'
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: 16, sm: 14 },
                        color: 'text.secondary'
                      }}
                    >
                      Referred for{' '}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: 16, sm: 14 },
                          fontWeight: 600,
                          color: 'text.primary'
                        }}
                      >
                        {referredForText}
                      </Typography>
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Stack gap={1}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}
        >
          Respond to this referral
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {ACTIONS.map((action) => {
            const isActive = status === action.status;
            const isPrimary = action.status === RecipientStatusEnum.Accepted;
            const { Icon } = action;
            return (
              <Button
                key={action.status}
                variant={isActive ? 'contained' : 'outlined'}
                color={action.color}
                startIcon={<Icon />}
                disabled={disabled}
                onClick={() => onSelect(action.status)}
                sx={(theme) => ({
                  minWidth: { sm: 168 },
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  },
                  [theme.breakpoints.down('md')]: {
                    flexBasis: isPrimary ? '100%' : 0,
                    flexGrow: 1,
                    minWidth: 0
                  },
                  [theme.breakpoints.down('sm')]: {
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': {
                      marginRight: theme.spacing(0.5)
                    }
                  }
                })}
              >
                {action.label}
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
}
